import type { PerceivedEvent } from '@/sim/events';
import { DAY } from '@/sim/clock';
import { createCard, type Card, type CardView } from './card';
import { MORTAL_VERBS, type MortalVerb } from './mortalVerbs';
import type { PlayerMode } from './inhabit';

/**
 * The quiet chrome around the world.
 *
 * The land is the primary interface (law 12) and this layer only frames it. It
 * used to frame it four times over: a hover line, a chosen line, a focus row, a
 * cost row, a band summary and a debug row all stacked in the top right, a
 * followed life top left, a place's history bottom left, and a two-storey verb
 * bar carrying five verbs and six faculties along the bottom. Understanding one
 * tree meant reading three corners of the screen and knowing which of them was
 * about the tree.
 *
 * What is left is four things, and each of them answers a different question:
 *
 *     top      the world has an age                     — a year, and nothing else
 *     centre   what is under my gaze, and what a cast would take — light, transient
 *     left     what I have chosen                       — `ui/card.ts`, one card
 *     bottom   what I can do about it                   — one line, four controls
 *
 * The Chronicle keeps the right-hand margin, because it is not about the
 * selection at all: it is the world talking about itself, streaming, and it is
 * the one surface here that would be wrong to fold into anything.
 *
 * It reads state each frame and owns none of it; every intent goes back through
 * a callback so main.ts stays the only writer.
 */

/**
 * What `Simulation.act` accepts. Grasp is deliberately NOT here: it takes hold
 * of matter and the simulation reaches it through `grasp`/`moveEarth`/
 * `shapeLand`, never through `act`. Keeping the two unions apart is what stops
 * a `sim.act('grasp', …)` from ever typechecking.
 */
export type CastVerb = 'strike' | 'rain' | 'strengthen' | 'weaken' | 'omen';

/** What the PALETTE can arm — the cast verbs plus Grasp (`DECISIONS.md`). */
export type Verb = CastVerb | 'grasp';

/** What a cast would take of the god. A category, never a number (III §113). */
export type CostBandName = 'low' | 'moderate' | 'high' | 'extreme';

export interface HudCallbacks {
  onFlyTo(x: number, z: number): void;
  /**
   * The Anchor, which is attendance rather than a cast — the same act `V`
   * performs, and it refuses from the air in the same words.
   */
  onAnchor(): void;
  /**
   * INHABIT — descend into the chosen life and walk the world at its height
   * (`ui/inhabit.ts`, `ui/mortalPresence.ts`). Not a verb: it is the other
   * body the player can be in, so it has its own control outside the row,
   * as the Anchor does. Pressed with nothing inhabitable chosen, main.ts
   * refuses in words and spends nothing.
   */
  onInhabit(): void;
  /** ASCEND — the same control while mortal: back to the deity, which is where it was. */
  onAscend(): void;
  /**
   * A mortal slot clicked in the bar — the same path `1`–`4` take in mortal
   * mode (`PlayerModes.pressSlot`), so the lit slot is one state however it
   * was lit. Optional: the slots do nothing yet and a bar without them is whole.
   */
  onMortalSlot?(slot: MortalVerb['slot']): void;
}

/** What the wheel has chosen: the scale of divine attention. */
export interface HudFocus {
  /** Band name — presence · local · grove · valley · watershed. */
  band: string;
  /** Cast radius in metres. */
  radius: number;
}

/**
 * One life, as the player is allowed to know it (v0.3.2 §3).
 *
 * Every field is a phrase, never a number, and there is deliberately nothing
 * here to act on. No orders, no assignment, no inventory, no percentage —
 * this exists so that a god can recognise somebody and find out later what
 * became of them, not so that anybody can be managed. The moment it grows a
 * button it has become the wrong game.
 */
export interface HudPerson {
  name: string;
  band: string;
  /** What they are doing right now, and who with. */
  doing: string;
  /** Whether they are walking it, and how far out from the fire they are. */
  where: string;
  /** How they are holding up, in words. */
  condition: string;
  /** Temperament and inclination — character, never a skill level. */
  temper: string;
  /** How long they have been with the band. */
  since: string;
  /**
   * What the inexplicable has already done to this life, or absent.
   *
   * `person.importance` rises only when a *divine* event is witnessed at close
   * range, so on a world generated with no god in it every soul is at zero and
   * this is undefined for all of them. It is therefore the one reading on a
   * person that is a record of the PLAYER — and it names no hand, because law
   * 10 means nothing witnessable ever can.
   */
  marked?: string;
  /**
   * WHO MATTERS TO THEM, in words — lineage and company, never a count.
   *
   * Empty for most people, and that is the truthful answer rather than a gap:
   * a person works with whoever the day assigns unless somebody genuinely
   * stands out, and `companionOf` refuses to name anybody who does not
   * (`tools/bonds.ts` §3 — a band re-draws its parties from the same two dozen
   * people, so an ungated list is an attendance register).
   *
   * A tie to somebody the world can no longer find is kept and said
   * differently. That is the point of the whole feature: when the person you
   * had been watching them work beside is gone, the card is where you find out
   * that something was lost.
   */
  ties: string[];
  /**
   * What they remember: an event id, its day, and — when the memory is
   * their own rather than the band's — how they came by it ("was there",
   * "heard it"). Personal witness leads; communal memory is the fallback.
   */
  remembers: Array<{ eventId: string; day: number; how?: string }>;
  /** What they have lately become — *has grown bold* — when they have. */
  becoming?: string;
  /** How they stand against their own band — *bolder than most here* — when they stand clear. */
  stands?: string;
  /** Where they keep going — *works the ground to the north, a short walk out, most days*. */
  haunt?: string;
  /**
   * WHAT A STRENGTHEN WOULD ACTUALLY TAKE HOLD OF, strongest first, in the
   * world's own words. Empty when there is nothing — which is a real answer
   * and not a gap.
   *
   * This is `people.ts`'s `asked(activity)` said out loud, and it is offered
   * because the gate it describes is already real and already invisible.
   * `press()` returns 0 before touching anybody whose party activity is not in
   * `WORK` — someone resting, travelling, fleeing or idle in camp cannot be
   * reinforced at all — and where it does bite, it writes ONLY the axes that
   * today's work asked for. A player had no way to see any of that; the cast
   * simply did nothing, and looked like a cast that had done something.
   *
   * It is deliberately not a menu, and it names no numbers. Law 14: saying
   * where causal pressure lands is not a promise about what results.
   */
  takes: string[];
}

export interface HudState {
  /** The world's age. The only clock the persistent chrome carries. */
  year: number;
  /** What the pointer is over, in the world's voice. Null over nothing. */
  looking: string | null;
  /** Where the cursor is, in client pixels — the aim cluster sits beside it. Absent: the centre. */
  aim?: { x: number; y: number };
  /**
   * Everything about what the god has CHOSEN — one card, on the left. Null
   * clears it. Hovering never fills this: selection is the commitment.
   */
  card: CardView | null;
  /** The scale a cast would have, shown only while it is being steered. */
  focus: HudFocus;
  /** True for a beat after the wheel moved, so the readout fades in and out. */
  focusFresh: boolean;
  /**
   * Stones a HELD verb key has gathered so far — what is about to be spent
   * when it comes up. 0 when nothing is held. The bar marks that many of the
   * lit stones as charging, so the god watches its own impulse being drawn.
   */
  charge?: number;
  /**
   * How much of itself the god has left, 0..1 — `GodState.power`, which is
   * `DESIGN.md` §13.2's Impulse under the name it already had in the snapshot.
   */
  power: number;
  /** How many stones this god has actually put down. Read from the world. */
  anchors: number;
  /**
   * …and how many of them are WORKING — `AuraSystem.lit()`, which had no
   * `src/` caller, so nothing in the game could tell a god where it had left
   * part of itself burning.
   *
   * Two numbers rather than one because they are two different facts and the
   * second is the expensive one: an anchor is a place you bound, and a lit
   * anchor is a place that is spending you while you are somewhere else.
   */
  lit: number;
  /**
   * What each verb would cost at the current focus and aim, as a category.
   * Design III §113: the preview is a band, never a number — a god should
   * learn that a watershed cast is dear, not read a decimal.
   */
  costs?: Partial<Record<Verb, CostBandName>>;
  /**
   * Why the armed verb costs what it does, in the land's own terms — "with
   * the land", "against the land", "a wide attention", "far from you". It is
   * the only place the 6.8× environment spread §15 measured is *learnable*
   * rather than merely true.
   */
  costWhy?: readonly string[];
  notice: string | null;
  /**
   * Which body the player is in (`ui/inhabit.ts`). The bar is two palettes
   * and shows one: the deity's — Anchor, the four verbs, the year, Impulse,
   * INHABIT — or the mortal's — ASCEND, four placeholder slots, the year, and
   * nothing a mortal does not possess. Never inferred here; told.
   */
  mode: PlayerMode;
  /** A living person is chosen, so INHABIT would take: the control lights. */
  inhabitable: boolean;
  /** Whose viewpoint this is, while mortal — said on the control's title only. */
  mortalName?: string;
  /** The mortal slot lit by 1–4, or null. Presentation; it does nothing. */
  mortalArmed?: MortalVerb['slot'] | null;
}

export interface Hud {
  /** The verb the palette has armed, if any. Plain clicks cast it. */
  readonly armed: Verb | null;
  /**
   * Arm a verb, or `null` to let go. The number keys are the primary control
   * (`DECISIONS.md`) and they need a way in: before this the only setter was
   * inside the palette's own click handler, so the bar could be operated by
   * mouse and by nothing else. Arming the verb already armed disarms it, which
   * is what the buttons have always done and what a player pressing `1` twice
   * expects.
   */
  arm(v: Verb | null): void;
  /** Let go of it. Escape's chain calls this; nothing else should. */
  disarm(): void;
  update(s: HudState): void;
  /**
   * The Chronicle takes *perceived* events only — what happened, never whose
   * hand. The chrome could not name the god behind a miracle if it wanted
   * to, because it is never told: a rival's storm and the player's own read
   * the same line. Pass the running record; new entries are diffed in by id.
   */
  pushEvents(events: readonly PerceivedEvent[]): void;
  /**
   * Forget this world's news — the feed, the cursor into it, and every phrase
   * the Chronicle has minted.
   *
   * The HUD keeps a **second** Chronicle cursor of its own (`lastEventId`),
   * with the same find-or-restart shape `main.ts` uses, plus `said` — the
   * id → phrase memory a chosen mortal's card reads to say what they carry.
   * Both are bound to one world's event ring, and event ids are a pure
   * function of type, world-time and emission order, so two worlds of the same
   * seed mint the SAME ids: the cursor does not dangle, it *collides*, and the
   * card of a person in the new world quietly narrates the old one's miracle.
   *
   * Called from `adoptWorld` and from nowhere else.
   */
  forgetEvents(): void;
}

/**
 * The palette, and what is deliberately not on it.
 *
 * **Four, on the number keys** — `DECISIONS.md`, which supersedes law 20's
 * and law 21's enumerations (both are annotated in place in `CLAUDE.md`). The
 * bar had drifted to eleven peers once and was cut to three; three was the
 * right shape and the wrong membership, because it exposed the two verbs that
 * act on ground and hid both verbs that act on *things*, which is most of what
 * a player wants to do.
 *
 * `key` is rendered now. It used to be dead data — the template dropped it —
 * so the bar carried the string `'alt'` for a year while telling nobody.
 *
 *  · **Strike** takes a slot. It was a double-click and nothing said so, which
 *    made the one verb with real immediate force the one verb a player was
 *    least likely to find. The gesture still works.
 *  · **Grasp** takes a slot, and absorbs **Earth** and **Shape**: one
 *    interaction, one key, at whatever scale the thing under it happens to be.
 *    They were one interaction wearing three names and presenting them as
 *    peers taught a model the game does not have. `G` and `Y` still work.
 *  · **Weaken** comes off the numbered bar and stays in the simulation
 *    entirely — `sim.act('weaken', …)` is untouched, it keeps its aura, it is
 *    still a depth destination. This is an exposure decision, not a deletion,
 *    and law 22 still forbids anything naming either direction moral.
 *  · **Rain** is a Signature, not a starter Expression (law 21 says so in as
 *    many words, and `leverage.ts` quotes it).
 *  · **Aura** is not castable at all. It is something the god *has* — a
 *    property of Presence, carried by an Anchor. A button for it was a
 *    category error.
 *  · **Anchor** keeps its own circular control outside this row, because it is
 *    not a verb: it is a commitment, and it is scarce.
 *  · **Become** is Tab, and one of the two major surfaces of the game.
 */
const VERBS: Array<{ v: Verb; glyph: string; name: string; key: string }> = [
  { v: 'strengthen', glyph: '❁', name: 'strengthen', key: '1' },
  { v: 'strike', glyph: '↯', name: 'strike', key: '2' },
  // the informational verb: it costs the land nothing and writes nothing in it
  { v: 'omen', glyph: '✷', name: 'omen', key: '3' },
  { v: 'grasp', glyph: '✋', name: 'grasp', key: '4' },
];

/**
 * THE ICONS — line-drawn, one stroke weight, gold by CSS. Each is what the
 * verb IS rather than a symbol for it: a shoot with the sun on it, a blade, an
 * eye that is being shown something, an open hand. `currentColor` throughout,
 * so the plate's states (held, hovered) recolour them without a second set.
 * The old text glyphs above are kept for the aim cluster and the card, which
 * set type rather than draw.
 */
const svg = (body: string, box = '0 0 24 24'): string =>
  `<svg viewBox="${box}" fill="none" stroke="currentColor" stroke-width="1.35" ` +
  `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

const ICON: Partial<Record<Verb, string>> = {
  // a shoot in a bowl of ground, and the sun over it
  strengthen: svg(
    '<path d="M4.5 15c1.8 3.2 4.6 4.8 7.5 4.8s5.7-1.6 7.5-4.8"/>' +
    '<path d="M12 15.5V10"/>' +
    '<path d="M12 10c-2.4.2-4.6-1.5-5-3.9 2.5-.2 4.6 1.5 5 3.9z"/>' +
    '<path d="M12 10c2.4.2 4.6-1.5 5-3.9-2.5-.2-4.6 1.5-5 3.9z"/>' +
    '<path d="M12 2.6v1.6M6.2 4.6l1.1 1.1M17.8 4.6l-1.1 1.1"/>'),
  // a blade point-up, its guard, its grip
  strike: svg(
    '<path d="M12 2.8l1.6 3.4v8.6L12 16.6l-1.6-1.8V6.2z"/>' +
    '<path d="M8.2 17.2h7.6"/>' +
    '<path d="M12 17.2v3.4"/>' +
    '<path d="M10.8 20.6h2.4"/>'),
  // an eye, and rays over it: something is being shown
  omen: svg(
    '<path d="M2.6 13.2s3.8-5.6 9.4-5.6 9.4 5.6 9.4 5.6-3.8 5.6-9.4 5.6-9.4-5.6-9.4-5.6z"/>' +
    '<circle cx="12" cy="13.2" r="2.3"/>' +
    '<path d="M12 2.6v2.2M6 4.4l1.3 1.6M18 4.4l-1.3 1.6M3.4 8.6l1.9.9M20.6 8.6l-1.9.9"/>'),
  // an open hand, palm toward you
  grasp: svg(
    '<path d="M8.6 12.6V6.8a1.35 1.35 0 0 1 2.7 0v4.6M11.3 6.2V5.2a1.35 1.35 0 0 1 2.7 0v6.2' +
    'M14 6.4a1.35 1.35 0 0 1 2.7 0v5.4M16.7 8.6a1.35 1.35 0 0 1 2.7 0v5.6' +
    'c0 3.9-2.7 6.6-6.9 6.6-3.1 0-4.6-1.5-6.5-4.2l-2.3-3.3a1.5 1.5 0 0 1 2.4-1.8l1.5 1.9"/>'),
};

/** The Anchor's own mark: a ring, a shank, its arms and the crossbar. */
const ANCHOR_ICON = svg(
  '<circle cx="12" cy="4.4" r="1.9"/>' +
  '<path d="M12 6.3v14.2"/>' +
  '<path d="M7.6 10.4h8.8"/>' +
  '<path d="M4.6 14.6c0 3.4 3.2 5.9 7.4 5.9s7.4-2.5 7.4-5.9"/>' +
  '<path d="M4.6 14.6l-1.5-1.5M4.6 14.6l1.9-.9M19.4 14.6l1.5-1.5M19.4 14.6l-1.9-.9"/>');

/**
 * The mortal palette's marks, in the same hand as the verbs': a hand closing
 * on a stone, a knapped point, a voice, an adze. Placeholders, like the slots
 * they sit on (`ui/mortalVerbs.ts`).
 */
const MORTAL_ICON: Record<MortalVerb['slot'], string> = {
  // take: a cupped hand and the stone it is closing on
  1: svg(
    '<circle cx="12" cy="6.2" r="2.4"/>' +
    '<path d="M5.5 13.5c1.2 3.6 3.6 6.5 6.5 6.5s5.3-2.9 6.5-6.5"/>' +
    '<path d="M7.2 12.4l2.2-2 2.6 2.8 2.6-2.8 2.2 2"/>'),
  // use: a knapped point on its haft
  2: svg(
    '<path d="M12 2.8l3.4 6.2L12 14l-3.4-5z"/>' +
    '<path d="M12 14v7.2"/>' +
    '<path d="M9.6 17.2h4.8"/>'),
  // speak: a voice going out
  3: svg(
    '<circle cx="7" cy="12" r="1.6"/>' +
    '<path d="M10.6 8.2a5.4 5.4 0 0 1 0 7.6"/>' +
    '<path d="M13.6 5.4a9.4 9.4 0 0 1 0 13.2"/>' +
    '<path d="M16.6 2.8a13.2 13.2 0 0 1 0 18.4"/>'),
  // work: an adze
  4: svg(
    '<path d="M5 20l8.6-8.6"/>' +
    '<path d="M11.2 7.8l5 5 2.6-2.6-5-5z"/>' +
    '<path d="M16.2 12.8l2.8 2.8"/>'),
};

/**
 * The other body's control: a standing figure, with a mark for the way the
 * player is about to go — down into it, or up out of it.
 */
const FIGURE =
  '<circle cx="11" cy="4.6" r="2.1"/>' +
  '<path d="M11 6.7v7.2"/>' +
  '<path d="M11 13.9l-3.4 6.4M11 13.9l3.4 6.4"/>' +
  '<path d="M6.6 10.2h8.8"/>';
const INHABIT_ICON = svg(FIGURE + '<path d="M19.4 3.2v5.4M17.6 6.8l1.8 1.8 1.8-1.8"/>');
const ASCEND_ICON = svg(FIGURE + '<path d="M19.4 8.6V3.2M17.6 5l1.8-1.8 1.8 1.8"/>');

/**
 * The flourish where the plate meets its own centre line — a rule drawn by
 * hand that carries a stone where the hand lifted, and two small curls beside
 * it. The same lozenge `.rule` uses on the card, so the two are one hand.
 */
const FLOURISH =
  '<svg viewBox="0 0 132 16" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">' +
  '<path d="M2 8h44c5.5 0 8.5-5 12-5" opacity=".7"/>' +
  '<path d="M130 8H86c-5.5 0-8.5-5-12-5" opacity=".7"/>' +
  '<path d="M66 1.6l3.6 6.4-3.6 6.4-3.6-6.4z" fill="currentColor" stroke="none"/>' +
  '<circle cx="53" cy="8" r="1.1" fill="currentColor" stroke="none" opacity=".8"/>' +
  '<circle cx="79" cy="8" r="1.1" fill="currentColor" stroke="none" opacity=".8"/>' +
  '</svg>';

/** Impulse, as five stones. */
const PIPS = 5;

/** Metres up to a kilometre, then kilometres — always three significant-ish. */
const metres = (m: number): string =>
  m < 1000 ? `${m < 100 ? m.toFixed(m < 20 ? 1 : 0) : Math.round(m)} m`
           : `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`;

const VANISHED: Partial<Record<string, string>> = {
  grazer: 'the herds have left this ground',
  browser: 'the browsers are gone from the scrub',
  small_game: 'the small game is gone from here',
  predator: 'nothing hunts this ground now',
  scavenger: 'the scavengers have moved on',
};
const RETURNED: Partial<Record<string, string>> = {
  grazer: 'the herds are back on this ground',
  browser: 'browsers have returned to the scrub',
  small_game: 'small game is back',
  predator: 'something hunts this ground again',
  scavenger: 'the scavengers have gathered again',
};

/** Events that read as wounds; the Chronicle inks them differently. */
/**
 * The Chronicle's day gutter: `d412`, or `d412–434` once a folded run has
 * crossed a day boundary.
 *
 * Module scope and exported because it is the half of the fold that can LIE.
 * Folding adjacent identical lines is safe; printing them under the first
 * day's label is not, because the natural runs span days — measured over the
 * shipped century, only 4 of 39 runs fall inside one world-day, the median
 * spans 3 and the longest 22. A 22-day run of herd movement under a single
 * `d412` asserts something that did not happen. This is the rule that keeps
 * the fold honest, so it is the rule with a test on it.
 */
export const dayLabel = (first: number, last: number): string =>
  `<span class="d">d${first}${last > first ? `–${last}` : ''}</span>`;

/**
 * TWO SETS NAMED `GRIM` AND THEY DISAGREED ABOUT A DEATH.
 *
 * `sim/mortals/band.ts` files `person_died` as a wound — its own docstring
 * says the whole point is that a memory system "cannot be met by … filing the
 * death of somebody you worked beside for ten years under news". This one did
 * not carry it, so the simulation weighted a death as grief and the Chronicle
 * inked it as ordinary news, on the same event, in the same frame.
 *
 * The sim's reading is the right one and the resolution is to match it. The
 * two sets stay separate rather than being shared, because they answer
 * different questions — the sim's decides how heavily a band REMEMBERS,
 * this one decides how a line READS — and a `src/sim` file may not import
 * `src/ui`. What they must never do is disagree about which events are
 * wounds, which is what a test can hold.
 *
 * `camp_abandoned` is here and not in the sim's set, and that is not the same
 * kind of difference: a camp left to the land is a melancholy line to read and
 * is not somebody's grief to carry.
 */
const GRIM = new Set(['fire_started', 'fire_burned', 'predator_incident',
                      'resource_shortage', 'person_died', 'camp_abandoned']);

/**
 * The world's record, in the world's voice; your acts carry their glyph.
 * Exported so a test can ask whether a band actually has anything sayable in
 * its memory — a life panel that is always blank would be worse than none.
 */
/**
 * What a crossing is called, per axis and direction — the same vocabulary
 * `ui/lives.ts`'s `temper()` uses for the standing state, conjugated as a
 * change. Nothing for an axis with no word in either direction.
 */
const BECAME: Partial<Record<string, string>> = {
  'courage:up': 'has grown bold', 'courage:down': 'has grown wary',
  'curiosity:up': 'has grown curious', 'curiosity:down': 'has settled',
  'sociability:up': 'has come to be well liked', 'sociability:down': 'has come to keep apart',
  'hunt:up': "has grown into a hunter's hands", 'hunt:down': 'has lost the hunter\'s hands they had',
  'forage:up': "has grown a forager's eye", 'forage:down': 'has lost the forager\'s eye they had',
  'build:up': "has grown into a builder's hands", 'build:down': 'has lost the builder\'s hands they had',
  'explore:up': "has grown a wanderer's feet", 'explore:down': 'has lost the wanderer\'s feet they had',
};

export function phrase(e: PerceivedEvent): string | null {
  switch (e.type) {
    // A cell emptied of a species, and one restocked after an absence — the
    // two crossings ROADMAP §42 added. Salience (tools/trajectory-migrations.ts):
    // returned = 0.5 + 0.06·min(years away, 5) + 0.1 for grazer/browser/
    // predator, so it is spoken after ~1.7 y away for those and 3.3 y for the
    // small kinds; vanished = 0.5 + 0.35·(head/greatMove). ~4 returns a year
    // island-wide on the shipped century; vanishings come from big burns and
    // Weaken.
    case 'wildlife_vanished':
      return e.salience < 0.7 ? null : (VANISHED[String(e.data?.species)] ?? 'the animals are gone from here');
    case 'wildlife_returned':
      return e.salience < 0.7 ? null : (RETURNED[String(e.data?.species)] ?? 'the animals are back');
    case 'divine_strike': return '⚡ lightning fell';
    case 'divine_rain': return '☂ rain was called down';
    // Observed, never judged. These said "blessed" and "cursed" while the
    // verbs were called Bloom and Wither, and Design IV §15 is explicit that
    // Weaken is not an evil ability — a god uses it to hold back invasive
    // growth, quiet a catastrophic fire or relieve an overpopulation. Morality
    // comes from subject, context and consequence; the verb never carries it,
    // so what a witness records is what the ground did.
    case 'divine_strengthen': return '❁ the land quickened';
    case 'divine_weaken': return '☾ the land failed';
    case 'divine_omen': return '✷ something was shown';
    // What a witness can honestly report: a thing that was there is here.
    // Enormous, unexplained, and — as ever — carrying nothing about the hand.
    case 'divine_grasp': return '✋ something was moved by nothing';
    // …AND THE GROUND-MOVING HALF OF THE SAME HAND, which the Chronicle has
    // been silent about since `moveEarth` got an input path. `divine_earth` is
    // the only DURABLE thing Grasp does — a hill somebody dug is there in a
    // century — and it was the one act in the game that left a permanent mark
    // and no line. Same law 10 discipline as the rest: what a witness could
    // report, never whose hand.
    case 'divine_earth': return '✋ the ground itself moved';
    // An aura is a god leaving part of itself burning in a place. What is
    // witnessable is that the place will not settle; when it finally gutters
    // out, what is witnessable is that it has.
    case 'divine_aura': return '◈ something here would not stay still';
    case 'divine_aura_spent': return '◈ whatever was in this place is spent';
    case 'anchor_placed': return '◆ a place was made to matter';
    case 'anchor_found': return 'they came upon the standing stone';
    case 'sacred_raised':
      return e.data?.stage === 'footpath' ? 'a path to the stone was worn'
        : e.data?.stage === 'offerings' ? 'they began leaving offerings'
        : e.data?.stage === 'cairn' ? 'they raised a cairn beside it'
        : e.data?.stage === 'shelter' ? 'they roofed the place'
        : e.data?.stage === 'shrine' ? 'the place became a shrine'
        : 'the place became a temple';
    case 'omen_answered':
      return e.data?.meaning === 'guidance' ? 'the sign led them to something'
        : e.data?.meaning === 'warning' ? 'the sign had been a warning'
        : 'they found nothing where the sign pointed';
    case 'fire_started': return 'a fire began';
    // a burn that finished, as one line — `magnitude` is hectares, and the
    // words are the ones a place uses about its scar (`ui/lore.ts`)
    case 'fire_burned':
      return e.magnitude >= 100 ? 'the fire burned out, and it took the whole hillside'
        : e.magnitude >= 10 ? 'the fire burned out, and it took a good piece of ground'
          : 'the fire burned out';
    case 'band_migrated': return 'the people set out for new ground';
    case 'band_split': return 'the band came apart, and some walked away';
    case 'band_merged': return 'two peoples became one';
    case 'culture_diverged':
      return e.data?.people ? `they are the ${e.data.people} now` : 'they are a people of their own now';
    case 'band_arrived': return 'the people made a new home';
    case 'structure_started': return `they began ${e.data?.type ?? 'building'}`;
    case 'structure_completed': return `they raised a ${String(e.data?.type ?? 'structure')}`;
    case 'path_formed': return 'a trail was worn';
    case 'camp_abandoned': return 'a camp was left to the land';
    case 'structure_claimed':
      return `they took up ${e.data?.structure === 'hearth' ? 'a cold hearth' : e.data?.structure === 'storage' ? 'an old granary' : 'an empty roof'}`;
    case 'predator_incident': return 'predators drew close';
    case 'resource_shortage':
      return e.data?.resource === 'water' ? 'thirst took hold' : 'hunger took hold';
    // A person, named, in the world's own voice — never a number about them,
    // which is the rule `test/follow.ts` already holds the card to.
    // The world's voice, and deliberately not "you consecrated a tree": law 10
    // means nobody in the world can say whose doing it was, and the Chronicle
    // is filtered to what the player could actually have known.
    case 'thing_promoted': {
      const k = String(e.data?.kind ?? 'thing');
      const what = k === 'plant' ? 'a tree' : k === 'rock' ? 'a stone' : 'a place';
      return e.data?.tended
        ? `${what} here is not ordinary any more`
        : `${what} here has been left wrong`;
    }
    case 'person_born':
      return `${e.data?.who ?? 'a child'} was born`;
    // somebody stopped being who they were — the card's own words for the
    // line they crossed (`STANDS_OUT` / `STANDS_APART`), in the world's voice
    case 'person_changed': {
      const who = String(e.data?.who ?? 'someone');
      const word = BECAME[`${String(e.data?.axis)}:${e.data?.up ? 'up' : 'down'}`];
      return word ? `${who} ${word}` : null;
    }
    case 'person_died': {
      const who = String(e.data?.who ?? 'someone');
      if (e.data?.of === 'want') return `${who} died, and there was not enough`;
      const age = Number(e.data?.age ?? 0);
      return age >= 45 ? `${who} died old`
        : age >= 15 ? `${who} died`
          : `${who} did not grow up`;
    }
    // herd movement is the land's weather — only the great migrations are news.
    // `data.fled` (ROADMAP §42): true is a herd leaving ground that collapsed
    // under it, false is a herd spilling toward a better neighbour
    case 'wildlife_migrated':
      return e.salience < 0.7 ? null
        : e.data?.fled === true ? 'the herds fled this ground' : 'the herds moved on';
    default: return null;
  }
}

/**
 * A memory whose record is gone, phrased from what its id still carries.
 *
 * `what they carry` was empty for every mortal at boot. The Chronicle starts
 * at the player's arrival — rightly: replaying a century of events to an
 * arriving god is OBSERVATION MUST NOT CREATE HISTORY pointed the other way —
 * and the card resolved a person's memories only through the phrases the
 * Chronicle had minted, so on a world with a hundred years behind it every
 * mind read *nothing yet worth keeping*. But a person's memory is theirs, not
 * the god's; pointing at a person and being told what they carry is law 12,
 * and reading it writes nothing. The section was not empty because they had
 * nothing — `tools/memory.ts` (2026-08-15): 719 of 720 living people carry a
 * memory at boot, 710 of them a full head of ten — it was empty because the
 * card could not say what the memories were.
 *
 * It can. An event id is `${type}@${time}#${seq}` (`sim/events.ts`), so the
 * kind and the moment outlive the record: measured, 4.7% of the shipped
 * memories still resolve against the ring, and **100% are phrasable from the
 * id alone.** So this is the fallback the card takes when the Chronicle has no
 * phrase for an id — and it is worded in a *worn* register on purpose. A
 * memory that has lost its particulars is not the memory the ring held: the
 * Chronicle says *Araren died old*; a head that has carried it for a year says
 * *someone died*. Same event, at two distances, exactly as `ui/lore.ts`'s
 * past tense sits behind `phrase()`'s present. Nothing here is invented — no
 * name, no cause, no number — and law 10 holds as it does above: the divine
 * kinds read as what a witness could report and nothing about a hand.
 *
 * One line is hedged on purpose. The shipped century was generated before
 * `person_died` existed and stored a death as `resource_shortage
 * {resource:'life'}` — `tools/memory.ts` puts 55% of what people carry at
 * boot under that type, and most of it is almost certainly deaths. From the
 * id alone the two cannot be told apart, so the faded line is *hard times at
 * the hearth*, which is true of both; a regenerated prehistory would carry the
 * real kind and this line would then be reserved for hunger.
 */
export function faded(eventId: string): { text: string; grim: boolean } | null {
  const at = eventId.indexOf('@');
  const type = at > 0 ? eventId.slice(0, at) : eventId;
  const text = FADED[type];
  return text ? { text, grim: GRIM.has(type) } : null;
}
const FADED: Partial<Record<string, string>> = {
  divine_strike: '⚡ lightning fell',
  divine_rain: '☂ rain was called down',
  divine_strengthen: '❁ the land quickened',
  divine_weaken: '☾ the land failed',
  divine_omen: '✷ something was shown',
  divine_grasp: '✋ something was moved by nothing',
  divine_earth: '✋ the ground itself moved',
  divine_aura: '◈ something here would not stay still',
  divine_aura_spent: '◈ whatever was in this place is spent',
  anchor_placed: '◆ a place was made to matter',
  anchor_found: 'they came upon the standing stone',
  sacred_raised: 'they built up the place they keep going back to',
  omen_answered: 'they went where the sign pointed',
  fire_started: 'a fire began',
  fire_burned: 'fire came through',
  band_migrated: 'the people set out for new ground',
  band_split: 'the band came apart',
  band_merged: 'two peoples became one',
  culture_diverged: 'they became a people of their own',
  band_arrived: 'the people made a new home',
  structure_started: 'they began building',
  structure_completed: 'they raised a roof',
  path_formed: 'a trail was worn',
  camp_abandoned: 'a camp was left to the land',
  structure_claimed: 'an old roof has new keepers',
  predator_incident: 'predators drew close',
  resource_shortage: 'hard times at the hearth',
  thing_promoted: 'something here stopped being ordinary',
  person_born: 'a child was born',
  person_died: 'someone died',
  person_changed: 'someone became other than they were',
  wildlife_migrated: 'the herds moved on',
  wildlife_vanished: 'the animals left this ground',
  wildlife_returned: 'the animals came back',
};

export function createHud(cb: HudCallbacks): Hud {
  const $ = (id: string) => document.getElementById(id)!;
  const timeEl = $('time'), lookEl = $('look'), chronEl = $('chronicle');
  const verbsEl = $('verbs'), noticeEl = $('notice');
  // the corner tip names Tab and Escape; on foot Tab does nothing, so it says
  // the two things a mortal can do with the chrome instead
  const tipEl = document.getElementById('tip');
  const deityTip = tipEl?.innerHTML ?? '';

  // — the year —
  //
  // A read-out, not a control, and now only the one number. Law 27: THE SHARED
  // WORLD HAS ONE CLOCK, and the day and the hour it used to carry are what the
  // *people* live in — the god lives in years. What the persistent chrome has
  // to say is *this world has an age*; the simulation clock is not the player's
  // business sixty times a second.
  timeEl.innerHTML = '<span class="clock"></span>';
  const clockSpan = timeEl.querySelector<HTMLElement>('.clock')!;
  let lastYear = -1;

  // — the bar: the god's reserve, the Anchor, and the four Expressions —
  //
  // One line. The Anchor is drawn as its own faculty rather than a fifth item
  // in a spell list, because it is not one: every Expression is a gesture from
  // wherever you happen to be, and this one is attendance — you have to come
  // down and stand on the spot, and it costs most of a month of the god.
  //
  // The number is drawn on the button. It is how the control is meant to be
  // used — the mouse is the accessible path, not the intended one — and a key
  // binding nothing on screen mentions is a key binding nobody presses.
  verbsEl.innerHTML =
    `<button class="anchor" title="Anchor — come down and stand where you mean it">` +
    `${ANCHOR_ICON}<span class="n">anchor</span>` +
    `<span class="count">0</span></button>` +
    `<div class="plate">` +
      `<span class="orn top">${FLOURISH}</span><span class="orn bottom">${FLOURISH}</span>` +
      `<span class="row deity">${VERBS.map((o, i) =>
        (i ? '<span class="dot"></span>' : '') +
        `<button data-v="${o.v}" title="${o.name} — key ${o.key}">` +
        `<span class="c"></span><span class="k">${o.key}</span>` +
        `${ICON[o.v] ?? ''}<span class="n">${o.name}</span></button>`).join('')}</span>` +
      // the mortal's four, drawn in the same hand and shown only while mortal.
      // Working labels, data-driven (`ui/mortalVerbs.ts`); they light and do nothing.
      `<span class="row mortal">${MORTAL_VERBS.map((o, i) =>
        (i ? '<span class="dot"></span>' : '') +
        `<button data-m="${o.slot}" title="${o.name} — key ${o.slot}">` +
        `<span class="k">${o.slot}</span>` +
        `${MORTAL_ICON[o.slot]}<span class="n">${o.name}</span></button>`).join('')}</span>` +
      `<span class="sep"></span>` +
      `<span class="clock"><span class="l">year</span><b>—</b></span>` +
      `<span class="sep impulse"></span>` +
      `<span class="impulse"><span class="l">impulse</span>` +
        `<span class="pips">${'<span>◆</span>'.repeat(PIPS)}</span></span>` +
    `</div>` +
    // the other body: a circle at the far end, the Anchor's twin — INHABIT
    // while deity, ASCEND while mortal. Its own control because it is not a
    // verb: it is which body the player is in (`ui/inhabit.ts`).
    `<button class="inhabit" title="Inhabit — choose a mortal, then walk the world at their height">` +
    `<span class="i enter">${INHABIT_ICON}</span><span class="i leave">${ASCEND_ICON}</span>` +
    `<span class="n">inhabit</span></button>`;

  let armed: Verb | null = null;
  const verbBtns = [...verbsEl.querySelectorAll<HTMLButtonElement>('button[data-v]')];
  const pipsEl = verbsEl.querySelector<HTMLElement>('.pips')!;
  const pipEls = [...pipsEl.querySelectorAll<HTMLElement>('span')];
  const yearEl = verbsEl.querySelector<HTMLElement>('.clock b')!;
  const anchorBtn = verbsEl.querySelector<HTMLButtonElement>('button.anchor')!;
  const anchorCount = anchorBtn.querySelector<HTMLElement>('.count')!;
  const inhabitBtn = verbsEl.querySelector<HTMLButtonElement>('button.inhabit')!;
  const inhabitLabel = inhabitBtn.querySelector<HTMLElement>('.n')!;
  const mortalBtns = [...verbsEl.querySelectorAll<HTMLButtonElement>('button[data-m]')];
  const paint = () => {
    for (const b of verbBtns) b.classList.toggle('on', b.dataset.v === armed);
  };
  for (const b of verbBtns) {
    b.addEventListener('click', () => {
      armed = armed === b.dataset.v ? null : (b.dataset.v as Verb);
      paint();
    });
  }
  anchorBtn.addEventListener('click', () => cb.onAnchor());
  // one control, two meanings, and the MODE decides which — told by main.ts
  // through `update`, never guessed from what the button looks like
  let mode: PlayerMode = 'deity';
  inhabitBtn.addEventListener('click', () => (mode === 'mortal' ? cb.onAscend() : cb.onInhabit()));
  // the mortal slots light from the keys through `pressSlot` in main.ts; a
  // click on one does the same thing through the same path, so the bar and
  // the keys cannot disagree about which slot is lit
  for (const b of mortalBtns) {
    b.addEventListener('click', () => cb.onMortalSlot?.(Number(b.dataset.m) as MortalVerb['slot']));
  }
  // Escape is NOT handled here. It runs a precedence chain in main.ts — close a
  // panel, else let go of what is held, else open the menu — and two
  // independent listeners would have made one keypress both disarm the palette
  // and open the menu.

  /** Paint the god's own reserve, and what each verb would take of it. */
  let lastAnchors = -1, lastLit = -1;
  let lastPips = -1;
  let lastInhabitable: boolean | null = null;
  let lastMortalArmed: MortalVerb['slot'] | null | undefined;
  let lastMortalName: string | undefined;
  const paintBar = (s: HudState) => {
    // THE PALETTE SWAP. The whole bar re-dresses on the mode: the anchor and
    // the Impulse go, the mortal row comes, the circle at the end turns from
    // INHABIT to ASCEND. CSS does the hiding off one class, so nothing a
    // mortal does not possess is ever painted while mortal.
    if (s.mode !== mode) {
      mode = s.mode;
      verbsEl.classList.toggle('mortal', mode === 'mortal');
      inhabitLabel.textContent = mode === 'mortal' ? 'ascend' : 'inhabit';
      if (tipEl) tipEl.innerHTML = mode === 'mortal' ? 'ascend · the bar &nbsp; esc · menu' : deityTip;
      lastInhabitable = null; lastMortalName = undefined;
    }
    if (mode === 'mortal') {
      if (s.mortalName !== lastMortalName) {
        lastMortalName = s.mortalName;
        inhabitBtn.title = s.mortalName
          ? `Ascend — leave ${s.mortalName} and return to where your eye was`
          : 'Ascend — return to where your eye was';
      }
      if (s.mortalArmed !== lastMortalArmed) {
        lastMortalArmed = s.mortalArmed;
        for (const b of mortalBtns) b.classList.toggle('on', Number(b.dataset.m) === s.mortalArmed);
      }
      return;
    }
    if (s.inhabitable !== lastInhabitable) {
      lastInhabitable = s.inhabitable;
      inhabitBtn.classList.toggle('ready', s.inhabitable);
      inhabitBtn.title = s.inhabitable
        ? 'Inhabit — walk the world at this life\'s height'
        : 'Inhabit — choose a mortal first';
    }
    // Impulse as stones: how many of the five are lit is the god's reserve,
    // rounded so a nearly-full god reads full — a percentage is not what a god
    // feels, and the last sliver of a stone is not a fact worth a pixel
    const pct = Math.max(0, Math.min(1, s.power));
    const lit = Math.round(pct * PIPS);
    // …and the charge: a held key draws stones from the top of the reserve,
    // and those stones burn brighter until the key comes up and they are gone
    const charging = Math.min(lit, Math.max(0, Math.round(s.charge ?? 0)));
    const key = lit * 16 + charging;
    if (key !== lastPips) {
      lastPips = key;
      pipEls.forEach((p, i) => {
        p.classList.toggle('on', i < lit);
        p.classList.toggle('charge', i >= lit - charging && i < lit);
      });
      pipsEl.classList.toggle('low', pct < 0.25);
    }
    for (const b of verbBtns) {
      const band = s.costs?.[b.dataset.v as Verb];
      const pip = b.querySelector<HTMLElement>('.c')!;
      pip.className = `c${band ? ' ' + band : ''}`;
    }
    // Driven by the world, never by the UI's own tally: the count is what
    // `sim.anchors` actually holds for this god, so a load, an absence or a
    // rival's world all report the truth rather than a number the chrome
    // incremented once and then believed.
    if (s.anchors !== lastAnchors || s.lit !== lastLit) {
      lastAnchors = s.anchors;
      lastLit = s.lit;
      // `3` while nothing burns, `2/3` while something does. The slash is the
      // whole message: some of what you have bound is currently costing you.
      anchorCount.textContent = s.lit > 0 ? `${s.lit}/${s.anchors}` : String(s.anchors);
      anchorBtn.classList.toggle('bound', s.anchors > 0);
      anchorBtn.classList.toggle('lit', s.lit > 0);
    }
  };

  // — chronicle —
  chronEl.innerHTML = '<header><span>chronicle</span><em>–</em></header><ul></ul>';
  const listEl = chronEl.querySelector<HTMLElement>('ul')!;
  const foldEl = chronEl.querySelector<HTMLElement>('header em')!;
  chronEl.querySelector('header')!.addEventListener('click', () => {
    chronEl.classList.toggle('closed');
    foldEl.textContent = chronEl.classList.contains('closed') ? '+' : '–';
  });
  let lastEventId = '';
  const MAX_LINES = 40;
  /**
   * Every phrase the Chronicle has ever minted, by event id. A band's memory
   * holds ids, not sentences, so this is what lets a chosen mortal's card say
   * what they carry without anyone re-walking the whole log.
   * Insertion order is emission order, so trimming the front trims the oldest.
   */
  const said = new Map<string, { text: string; grim: boolean }>();
  const SAID_MAX = 3000;

  /**
   * The run currently at the bottom of the feed, or null. Only the LAST row is
   * ever a fold candidate, which is what keeps the feed honest: two runs of
   * the same phrase separated by a different event stay two runs, in order.
   */
  let fold:
    | { el: HTMLLIElement; text: string; firstDay: number; lastDay: number;
        n: number; x: number; z: number }
    | null = null;

  const pushEvents = (events: readonly PerceivedEvent[]) => {
    // find where we left off; the log is append-only and capped, so if our
    // marker fell off the end, everything present is new enough
    let start = 0;
    if (lastEventId) {
      const i = events.findIndex((e) => e.id === lastEventId);
      start = i >= 0 ? i + 1 : 0;
    }
    let scrolled = false;
    for (let i = start; i < events.length; i++) {
      const e = events[i];
      const text = phrase(e);
      // Only what was actually SAID is kept. This used to store a null for
      // every unspoken event too, and `tools/memory.ts` (D, on the integrated
      // tree) showed what that cost: the map filled mostly with unspoken herd
      // noise, the oldest third was dropped every few hours, and phrased
      // lines a person's card needed were evicted with it — the share of
      // people whose card showed a line fell 24% → 17% across a session. An
      // id that is not here falls to `faded()`, the worn register, which is
      // the right answer for an evicted line AND for one the Chronicle
      // declined to speak — a mind that kept a small herd movement did keep
      // it.
      if (text && !said.has(e.id)) {
        said.set(e.id, { text, grim: GRIM.has(e.type) });
        if (said.size > SAID_MAX) {
          const it = said.keys();
          for (let k = 0; k < SAID_MAX / 3; k++) said.delete(it.next().value!);
        }
      }
      if (!text) continue;
      const day = Math.floor(e.time / DAY);

      // THE FOLD. Adjacent identical lines become one row with a count.
      //
      // Measured on the shipped century (`tools/feedrepeat.ts`, 365 god-free
      // world-days): 68.1% of everything the feed says sits inside a run of
      // two or more identical lines, and one phrase — "the herds moved on" —
      // is 69% of the whole feed on its own, in 45 separate runs. One player
      // action does it too: a wide Strike emits one `fire_started` per lit
      // bolt at the same world-second, so a single cast printed four
      // identical lines. That is a log, not a world speaking.
      //
      // THE DAY LABEL HAS TO BECOME A RANGE, and this is the load-bearing
      // half. The natural runs span DAYS — only 4 of 39 fall inside one
      // world-day, the median spans 3 and the longest 22 — so folding a
      // 22-day run of herd movement under a single `d412` would assert
      // something false. Nothing is erased, nothing is reordered, and no run
      // is ever collapsed across an intervening different event; the feed says
      // exactly what happened, in the order it happened, more briefly.
      if (fold && fold.text === text && fold.el.isConnected) {
        fold.n++;
        fold.lastDay = day;
        // the newest place, not the first: a fold is a live run, and clicking
        // it should go where the world is doing this NOW
        fold.x = e.x; fold.z = e.z;
        fold.el.innerHTML = dayLabel(fold.firstDay, fold.lastDay) + text +
          `<span class="n">×${fold.n}</span>`;
        scrolled = true;
        continue;
      }

      const li = document.createElement('li');
      if (GRIM.has(e.type)) li.classList.add('grim');
      if (e.type.startsWith('divine_')) li.classList.add('divine');
      li.innerHTML = dayLabel(day, day) + text;
      const run = { el: li, text, firstDay: day, lastDay: day, n: 1, x: e.x, z: e.z };
      fold = run;
      // reads `run` live, so a folded row flies to the newest place in its run
      li.addEventListener('click', () => cb.onFlyTo(run.x, run.z));
      listEl.appendChild(li);
      while (listEl.children.length > MAX_LINES) listEl.firstElementChild!.remove();
      scrolled = true;
    }
    if (events.length) lastEventId = events[events.length - 1].id;
    if (scrolled) listEl.scrollTop = listEl.scrollHeight;
  };

  /** The other half of adopting a world — see `Hud.forgetEvents`. */
  const forgetEvents = () => {
    lastEventId = '';
    said.clear();
    fold = null;
    listEl.innerHTML = '';
  };

  // — the one card, on the left —
  // What the Chronicle said about an event wins; anything else — a memory
  // older than the arrival, an evicted line, a movement the feed declined to
  // speak — falls through to the worn register.
  const card: Card = createCard((id) => said.get(id) ?? faded(id));

  // — what the cursor is on, beside the hand —
  //
  // Hover is deliberately much lighter than selection. It answers *what is
  // that* in one phrase and stops; the full card costs a click, because
  // SELECTION IS THE COMMITMENT. Opening a panel because a pointer crossed
  // something is how the screen filled up in the first place.
  let lastLook = '';
  const escape = (t: string) => t.replace(/[<&]/g, (c) => (c === '<' ? '&lt;' : '&amp;'));

  // — per-frame state —
  let lastNotice: string | null = null;
  const update = (s: HudState) => {
    card.update(s.card);
    paintBar(s);

    if (s.year !== lastYear) {
      lastYear = s.year;
      clockSpan.innerHTML = `year <b>${s.year}</b>`;
      yearEl.textContent = String(s.year);
    }

    // The aim cluster: what is under the gaze, what an armed verb would take
    // of you and why, and the scale it would take it at. All three are about
    // the same point in the world, so they read as one thing under the
    // crosshair rather than as three rows in a corner — and every one of them
    // is transient. Nothing is armed, nothing is hovered, the wheel is still:
    // the screen is the world.
    const armedRow = armed
      ? `<div class="act ${s.costs?.[armed] ?? ''}">` +
        `<span class="glyph">${VERBS.find((v) => v.v === armed)?.glyph ?? ''}</span>` +
        `<span class="what">${armed}</span>` +
        `<span class="band">${s.costs?.[armed] ?? '—'}</span></div>` +
        (s.costWhy?.length ? `<div class="why">${s.costWhy.join(' · ')}</div>` : '')
      : '';
    const focusRow = armed || s.focusFresh
      ? `<div class="focus">◎ ${s.focus.band} · ${metres(s.focus.radius)}</div>` : '';
    const look =
      (s.looking ? `<div class="name">${escape(s.looking)}</div>` : '') + armedRow + focusRow;
    if (look !== lastLook) {
      lastLook = look;
      lookEl.innerHTML = look;
      lookEl.classList.toggle('show', look !== '');
    }
    // The cluster follows the cursor: just below and to the right of the hand,
    // kept inside the frame, so a name never sits under the pointer that is
    // asking for it. Without a cursor it stays at the centre.
    if (s.aim) {
      const pad = 18;
      const w = lookEl.offsetWidth || 240, h = lookEl.offsetHeight || 40;
      const x = Math.min(Math.max(s.aim.x + pad, 8), innerWidth - w - 8);
      const y = Math.min(Math.max(s.aim.y + pad, 8), innerHeight - h - 8);
      lookEl.style.left = `${x}px`;
      lookEl.style.top = `${y}px`;
      lookEl.style.transform = 'none';
      lookEl.style.textAlign = 'left';
    }

    if (s.notice !== lastNotice) {
      lastNotice = s.notice;
      if (s.notice) noticeEl.textContent = s.notice;
      noticeEl.classList.toggle('show', s.notice !== null);
    }
  };

  return {
    get armed() { return armed; },
    arm(v: Verb | null) {
      // pressing the armed verb's own key lets go of it, exactly as clicking
      // its button always has — one rule, two inputs
      const next = v !== null && armed === v ? null : v;
      if (next === armed) return;
      armed = next;
      paint();
    },
    disarm() { if (armed) { armed = null; paint(); } },
    update,
    pushEvents,
    forgetEvents,
  };
}
